// Linked videos & studios shown inside the CRM Edit modal.
// (The richer versions in CRMPreview load their own data and are styled differently.)
import React, { useEffect, useState } from "react";
import { Film, Building2, DollarSign } from "lucide-react";
import { api } from "../../lib/api";

export function LinkedVideos({ crmId }) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/api/videos/by-crm/${crmId}`)
      .then(r => { if (active) setVideos(r.data); })
      .catch(() => { if (active) setVideos([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [crmId]);
  return (
    <div className="mt-4 border-t border-zinc-900 pt-4" data-testid="crm-linked-videos">
      <div className="overline mb-2 flex items-center gap-2">
        <Film size={12} /> LINKED CATALOG VIDEOS · {videos.length}
      </div>
      {loading && <div className="text-[12px] text-zinc-600 italic">Loading…</div>}
      {!loading && videos.length === 0 && (
        <div className="text-[12px] text-zinc-600 italic">No videos linked. Open a video in the catalog and use the cast linker to connect.</div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {videos.map(v => (
          <a key={v.id} href={`/catalog?focus=${v.id}`} target="_blank" rel="noreferrer"
            data-testid={`crm-lv-${v.id}`}
            className="block border border-zinc-900 rounded overflow-hidden hover:border-[color:var(--gio-primary)] transition">
            {v.mux_playback_id ? (
              <img src={`https://image.mux.com/${v.mux_playback_id}/thumbnail.jpg?width=160&height=96&fit_mode=smartcrop&time=2`}
                alt="" className="w-full h-20 object-cover" />
            ) : (
              <div className="w-full h-20 flex items-center justify-center bg-zinc-900">
                <Film size={20} className="text-zinc-700" />
              </div>
            )}
            <div className="p-1.5">
              <div className="text-[11px] text-white truncate">{v.title}</div>
              <div className="text-[10px] text-zinc-500 truncate">{v.cast || "—"}</div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

export function LinkedStudios({ crmId }) {
  const [studios, setStudios] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/api/studios/by-crm/${crmId}`)
      .then(r => { if (active) setStudios(r.data); })
      .catch(() => { if (active) setStudios([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [crmId]);
  const totalRevenue = studios.reduce(
    (acc, s) => acc + (s.scenes || []).reduce((a, sc) => a + (Number(sc.revenue) || 0), 0),
    0
  );
  return (
    <div className="mt-4 border-t border-zinc-900 pt-4" data-testid="crm-linked-studios">
      <div className="overline mb-2 flex items-center gap-2">
        <Building2 size={12} /> LINKED STUDIO COLLABS · {studios.length}
        {totalRevenue > 0 && (
          <span className="ml-auto inline-flex items-center gap-1 text-[color:var(--gio-secondary)]">
            <DollarSign size={11} /> {totalRevenue.toLocaleString()}
          </span>
        )}
      </div>
      {loading && <div className="text-[12px] text-zinc-600 italic">Loading…</div>}
      {!loading && studios.length === 0 && (
        <div className="text-[12px] text-zinc-600 italic">No studios linked. Open a studio entry and set this CRM record as the linked actor.</div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {studios.map(s => {
          const sceneCount = (s.scenes || []).length;
          const studioRev = (s.scenes || []).reduce((a, sc) => a + (Number(sc.revenue) || 0), 0);
          const cover = s.cover_image
            || (s.scenes?.[0]?.mux_playback_id ? `https://image.mux.com/${s.scenes[0].mux_playback_id}/thumbnail.jpg?width=160&height=96&fit_mode=smartcrop` : null)
            || s.photos?.[0];
          return (
            <a key={s.id} href={`/studios?focus=${s.id}`} target="_blank" rel="noreferrer"
              data-testid={`crm-ls-${s.id}`}
              className="flex gap-2 border border-zinc-900 rounded overflow-hidden hover:border-[color:var(--gio-primary)] transition">
              {cover ? (
                <img src={cover} alt="" className="w-20 h-16 object-cover shrink-0" />
              ) : (
                <div className="w-20 h-16 flex items-center justify-center bg-zinc-900 shrink-0">
                  <Building2 size={18} className="text-zinc-700" />
                </div>
              )}
              <div className="flex-1 min-w-0 p-2">
                <div className="text-[12px] text-white truncate font-bold">{s.studio_name}</div>
                <div className="flex items-center gap-2 text-[10px] mt-0.5" style={{ color: "var(--gio-muted-2)" }}>
                  <span>{s.status}</span>
                  <span>·</span>
                  <span>{sceneCount} scene{sceneCount !== 1 ? "s" : ""}</span>
                  {studioRev > 0 && (
                    <>
                      <span>·</span>
                      <span style={{ color: "var(--gio-secondary)" }}>{studioRev.toLocaleString()} {s.fee_currency}</span>
                    </>
                  )}
                </div>
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
}
